import React from 'react';
import PropTypes from 'prop-types';
import { ToastContainer } from 'react-toastify';
import PageNavbar from 'components/PartnerPage/PageNavbar';
import HeroSectionLayout from 'components/PartnerPage/HeroSectionLayout';
import Footer from 'components/PartnerPage/Footer';
import SectionFeaturesGrid from 'components/PartnerPage/SectionFeaturesGrid';
import SectionRawHtml from 'components/PartnerPage/SectionRawHtml';
import Newsletter from 'components/PartnerPage/Newsletter';
import ShipAndPlayPromise from 'components/PartnerPage/ShipPlayPromise';
import LeadSection from 'components/PartnerPage/LeadSection';
import { EmailInputProvider } from '../../contexts/EmailInputContext';

import CruiseQuickQuote from 'components/CruiseQuickQuote/CruiseQuickQuote';
import PageQuickQuote from 'components/QuickQuote/PageQuickQuote';
import { PageQuickQuoteProvider } from 'contexts/PageQuickQuoteContext';
import parse from 'html-react-parser';
import SectionShipOverview from 'components/PartnerPage/SectionShipOverview';
import HeroSidebar from 'components/PartnerPage/HeroSidebar';
import ImageSlider from 'components/PartnerPage/ImageSlider';
import SectionBenefits from 'components/PartnerPage/SectionBenefits';
import SectionOurServices from 'components/PartnerPage/SectionOurServices';
import SectionSplitComponent from 'components/PartnerPage/SectionSplitComponent';
import LandingPageReviewSection from 'components/PartnerPage/LandingPageReviewSection';
import BasicHeroSection from 'components/PartnerPage/BasicHeroSection';
import RegistrationHeroSection from 'components/PartnerPage/RegistrationHeroSection';
import RowIconTitleCaptionButtonSection from 'components/PartnerPage/RowIconTitleCaptionButtonSection';
import TravelAdvisorSignUpForm from 'components/PartnerPage/TravelAdvisorSignUpForm';
import BookingWidget from 'components/PartnerPage/BookingWidget';
import HorizontalHeader from 'components/PartnerPage/HorizontalHeader';
import SectionHowItWorks from 'components/PartnerPage/SectionHowItWorks';
import SectionReviews from 'components/PartnerPage/SectionReviews';
import SectionStats from 'components/PartnerPage/SectionStats';
import SectionConciergeContact from 'components/PartnerPage/SectionConciergeContact';
import SectionTestimonials from 'components/PartnerPage/SectionTestimonials';
import SectionSmsOptIn from 'components/PartnerPage/SectionSmsOptIn';

const generateCTAPath = (couponCode, defaultPath, subDomain) => {
  // Preserve existing URL params (TAC params, agent_id, etc.)
  const currentParams = new URLSearchParams(window.location.search);
  const newParams = new URLSearchParams();

  // Preserve TAC and other important params
  const preserveKeys = [
    'agent_id', 'ta_name', 'ta_email', 'ta_network', 'ta_company',
    'ta_agency', 'iata_number', 'utm_source', 'utm_medium', 'utm_campaign',
    // Address params (GSE-10993)
    'o_address', 'o_city', 'o_state', 'o_zip', 'o_country', 'o_company', 'o_apt',
    'd_address', 'd_city', 'd_state', 'd_zip', 'd_country', 'd_company', 'd_apt',
    // Facility params
    'shipping_from', 'shipping_to', 'o_facility_id', 'd_facility_id',
    // Cruise params
    'o_cruise_line', 'o_cruise_port', 'o_cruise_itinerary',
    'd_cruise_line', 'd_cruise_port', 'd_cruise_itinerary',
    // Product pre-population
    'products'
  ];

  preserveKeys.forEach(key => {
    const value = currentParams.get(key);
    if (value) {
      newParams.set(key, value);
    }
  });

  // Add or override coupon code
  if (couponCode) {
    newParams.set('coupon', couponCode);
  }

  if (subDomain) {
    newParams.set('partner', subDomain);
  }

  const basePath = couponCode ? '/ship' : defaultPath;
  const queryString = newParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

function Page({
  partnerId,
  partnerData,
}) {
  const coupon = new URLSearchParams(window.location.search).get('coupon');
  const subDomain = partnerData.microSite?.subDomain;

  const backgroundImageStyles = {
    '--background-image-mobile': `url(${partnerData.backgroundMobile.url})`,
    '--background-image-desktop': `url(${partnerData.backgroundDesktop.url})`,
    backgroundPosition: `${partnerData.backgroundPosition || '0 0'}`
  };

  return (
    <>
      <ToastContainer />
      <div className="relative font-poppins bg-cover bg-center backgroundImage" 
      style={partnerData.typeOf !== 'tac_landing' && partnerData.typeOf 
      !== 'registration' ? backgroundImageStyles : {}}>
        <PageNavbar
          NavLogo={partnerData.navLogo.url}
          subdomain={partnerData.subdomain || undefined}
          logoClasses={partnerData.logoClasses}
          showExtraLinks={partnerData.showNavExtraLinks}
          menuItems={partnerData.menuItems}
          typeOf={partnerData.typeOf}
        />
        {partnerData.hero && (
        <div
          style={backgroundImageStyles}
          className={`relative font-poppins bg-cover bg-center
            ${partnerData.typeOf === 'tac_landing' || partnerData.typeOf === 'registration' ? 'backgroundImage' : ''}
            m-2 mx-0 mt-0 rounded-none md:mx-2 md:rounded-2xl`}
        >
        <HeroSectionLayout
          pageType={partnerData.typeOf}
        >
          {partnerData.typeOf == 'cruise' && (
            <PageQuickQuoteProvider allowedLocations={window.gon?.allowedLocations}>
              <CruiseQuickQuote
                className="md:w-10/12 lg:w-full xl:w-6/12 2xl:4/12"
                fromTitle={partnerData.hero.subheader}
                toTitle={partnerData.hero.widgetHeader}
                couponCode={partnerData.couponCode}
                coupon={coupon}
                subDomain={subDomain}
                breakText={partnerData.hero.breakText}
                buttonText={partnerData.hero.buttonText}
              />
            </PageQuickQuoteProvider>
          )}

          {partnerData.typeOf == 'quick_quote' && (
            <>
              <HeroSidebar
                header={partnerData.hero.header}
                subheader={partnerData.hero.subheader}
              />

              <PageQuickQuoteProvider allowedLocations={window.gon?.allowedLocations}>
                <PageQuickQuote
                  className ={`md:w-10/12 lg:w-full xl:w-6/12 2xl:4/12 ${partnerData.widgetCustomClasses}`}
                  title = {partnerData.hero.widgetHeader}
                  couponCode= {partnerData.couponCode}
                  coupon={coupon}
                  subDomain={subDomain}
                  buttonText= {partnerData.hero.buttonText}
                  breakText={partnerData.hero.breakText}
                  quickQuoteUrl={partnerData.quickQuoteUrl}
                  microSiteId={partnerData.microSite?.id}
                  allowedOriginLocations={partnerData.lockOriginClubs ? partnerData.allowedOriginLocations : null}
                  toggleOriginDestination={partnerData.toggleOriginDestination}
                  allowedDestinationLocations={
                    partnerData.lockDestinationClubs ? partnerData.allowedDestinationLocations : null }
                  buttonCustomClasses={partnerData.hero.buttonCustomClasses}
                ></PageQuickQuote>
              </PageQuickQuoteProvider>
            </>
          )}
          {partnerData.typeOf == 'lead' && (
            <EmailInputProvider>
              <LeadSection
                heading={partnerData.hero.header}
                subheading={partnerData.hero.subheader}
                buttonCustomClasses={partnerData.hero.buttonCustomClasses}
                buttonText={partnerData.hero.buttonText}
              />
            </EmailInputProvider>
          )}
          {partnerData.typeOf == 'basic_hero' && (
            <BasicHeroSection
              heading={partnerData.hero.header}
              subheading={partnerData.hero.subheader}
            />
          )}
          {partnerData.typeOf == 'registration' && (
            <RegistrationHeroSection
              heading={partnerData.hero.header}
              subheading={partnerData.hero.subheader}
              buttonText={partnerData.hero.buttonText}
              url={partnerData.hero.url}
            />
          )}
          {partnerData.typeOf == 'tac_landing' && (
            <>
            <HorizontalHeader h1={partnerData.hero.h1} h2={partnerData.hero.h2}
                              h3={partnerData.hero.h3} />
            <BookingWidget
              subHeading={partnerData.hero.h4}
              onCtaClick={() => {
                window.location.href = generateCTAPath(
                  coupon || partnerData.couponCode, partnerData.shipNowUrl, subDomain
                );
              }}
              ctaText={partnerData.hero.buttonText}
              phoneNumber={partnerData.hero.breakText}
              limitToCityState={partnerData.hero.limitToCityState}
              url={partnerData.hero.url}
              address={partnerData.address}
              lockOriginOutboundLeg={partnerData.lockOriginOutboundLeg}
              lockDestinationOutboundLeg={partnerData.lockDestinationOutboundLeg}
              lockOriginReturnLeg={partnerData.lockOriginReturnLeg}
              lockDestinationReturnLeg={partnerData.lockDestinationReturnLeg}
              addressIsEditable={partnerData.addressIsEditable}
              subDomain={subDomain}
              couponCode={partnerData.couponCode}
              coupon={coupon}
            />
            </>
          )}
        </HeroSectionLayout>
        </div>
        )}
      </div>

      <main className="flex flex-col items-center justify-between w-full font-poppins bg-gray-100">
        {partnerData.sections.map((section, index) => {
          if (section.typeOf === 'ship_overview') {
            return (
              <SectionShipOverview
                key={index}
                ctaConfig={partnerData.ctaConfig}
                shipNowUrl={partnerData.shipNowUrl}
                shipNowButtonText={partnerData.shipNowButtonText}
                shipToUrl={partnerData.shipToUrl}
                shipToButtonText={partnerData.shipToButtonText}
                shipFromUrl={partnerData.shipFromUrl}
                shipFromButtonText={partnerData.shipFromButtonText}
                couponCode={partnerData.couponCode}
                coupon={coupon}
                subDomain={subDomain}
                name={partnerData.name}
                partnerId={partnerId}
                items={section.items}
                image={section.image}
                imagePosition={section.visualAlignment || 'right'}
                imageMobile={section.imageMobile}
                customClass={section.customClass}
              >
                <p className="font-bold text-[#1C2938] mb-5">
                  {parse(section.header || '')}
                </p>
                <div className="text-[#231F20] leading-[30px] mt-7 first:mt-0">
                  {parse(section.body)}
                </div>
              </SectionShipOverview>
            );
          }
          if (section.typeOf === 'features_grid') {
            return (
              <SectionFeaturesGrid
                key={index}
                header={section.header}
                headerClasses={section.headerClasses}
                subheader={section.subheader}
                subheaderClasses={section.subheaderClasses}
                items={section.items}
                customClass={section.customClasses}
              />
            );
          }
          if (section.typeOf == 'our_services') {
            return (
              <SectionOurServices
                key={index}
                section={section}
                visualAlignment={section.visualAlignment || 'right'}
              />
            )
          }
          if (section.typeOf == 'benefits') {
            return (
              <SectionBenefits key={index}
                section={section}
                visualAlignment={section.visualAlignment || 'right'}
              />
            )
          }

          if (section.typeOf == 'split') {
            return (
              <SectionSplitComponent
                key={index}
                section={section}
                visualAlignment={section.visualAlignment || 'right'}
                />
              )
            }
          if (section.typeOf == 'landing_reviews') {
            return (
              <LandingPageReviewSection
                key={index}
                section={section}
              />
            )
          }
          if (section.typeOf == 'raw_html') {
            return (
              <SectionRawHtml key={index} body={section.body}></SectionRawHtml>
            )
          }
          if (section.typeOf == 'image_slider') {
            return (
              <ImageSlider
                key={index} header={section.header} headerClasses={section.headerClasses}
                items={section.items} animate={section.animate}
                backgroundColor={section.backgroundColor}
              />
            )
          }
          if (section.typeOf == 'row_icon_title_caption_button') {
            return (
              <RowIconTitleCaptionButtonSection
                key={index}
                header={section.header}
                headerClasses={section.headerClasses}
                backgroundDesktop={section.backgroundDesktop}
                backgroundColor={section.backgroundColor}
                backgroundPosition={section.backgroundPosition}
                items={section.items}
                containerClasses={section.containerClasses}
              />
            )
          }
          if (section.typeOf === 'reviews') {
            return (
              <SectionReviews key={index} section={section} />
            );
          }
          if (section.typeOf === 'stats') {
            return (
              <SectionStats
                key={index}
                header={section.header}
                items={section.items}
                containerClasses={section.containerClasses}
                backgroundColor={section.backgroundColor}
              />
            );
          }
          if (section.typeOf === 'form') {
            if (section.formType === 'travel_agent') {
              return (
                <TravelAdvisorSignUpForm key={index} htmlId={section.htmlId} ctaButtonText={section.ctaButtonText} 
                ctaButtonLink={section.ctaButtonLink} />
              );
            }
          }
          if (section.typeOf === 'how_it_works') {
            return (
              <SectionHowItWorks
                key={index}
                header={section.header}
                items={section.items}
                containerClasses={section.containerClasses}
                backgroundColor={section.backgroundColor}
              />
            );
          }
          if (section.typeOf === 'concierge_contact') {
            return (
              <SectionConciergeContact
                key={index}
                header={section.header}
                headerClasses={section.headerClasses}
                subheaderClasses={section.subheaderClasses}
                subheader={section.subheader}
                items={section.items}
                body={section.body}
                containerClasses={section.containerClasses}
                backgroundColor={section.backgroundColor}
              />
            );
          }
          if (section.typeOf === 'sms_opt_in') {
            return (
              <SectionSmsOptIn
                key={index}
                ctaButtonText={section.ctaButtonText}
                smsCheckboxText={section.smsCheckboxText}
                smsLegalFootnote={section.smsLegalFootnote}
                containerClasses={section.containerClasses}
                backgroundColor={section.backgroundColor}
              />
            );
          }
          if (section.typeOf === 'testimonials') {
            return (
              <SectionTestimonials
                key={index}
                header={section.header}
                items={section.items}
                containerClasses={section.containerClasses}
                backgroundColor={section.backgroundColor}
              />
            );
          }

          return null;
        })}

        {partnerData.showPromise && (
          <ShipAndPlayPromise
            buttonCTAPath={generateCTAPath(coupon || partnerData.couponCode, partnerData.shipNowUrl, subDomain)}
            customClass={partnerData.promiseCustomClasses}
          />
         )}
      </main>

      {partnerData.footer && (
        <Footer
          header={partnerData.footer.header}
          body={partnerData.footer.body}
          brand={partnerData.brand.name}
          shipLinkPath={generateCTAPath(coupon || partnerData.couponCode, '/ship', subDomain)}
          subdomain={partnerData.subdomain || undefined}
          supportPhoneNumber={partnerData.footer.supportPhoneNumber}
          brandPhoneNumber={partnerData.brand.custServPhoneNumber}
          showNewsletter={partnerData.footer.showNewsletter}
        >
          {partnerData.footer.showNewsletter && <Newsletter />}
        </Footer>
      )}
      {partnerData.javascript && (
        <script dangerouslySetInnerHTML={{ __html: eval(partnerData.javascript) }}></script>
      )}
    </>
  );
}

Page.propTypes = {
  partnerId: PropTypes.string.isRequired,
  partnerData: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    brand: PropTypes.shape({
      name: PropTypes.string,
      logoUrl: PropTypes.string,
      custServPhoneNumber: PropTypes.string,
    }).isRequired,
    typeOf: PropTypes.string.isRequired,
    quickQuoteUrl: PropTypes.string,
    subdomain: PropTypes.string,
    couponCode: PropTypes.string,
    widgetCustomClasses: PropTypes.string,
    showNavExtraLinks: PropTypes.bool,
    logoClasses: PropTypes.string,
    showPromise: PropTypes.bool,
    promiseCustomClasses: PropTypes.string,
    lockOriginClubs: PropTypes.bool,
    lockDestinationClubs: PropTypes.bool,
    toggleOriginDestination: PropTypes.bool,
    address: PropTypes.object,
    lockOriginOutboundLeg: PropTypes.bool,
    lockDestinationOutboundLeg: PropTypes.bool,
    lockOriginReturnLeg: PropTypes.bool,
    lockDestinationReturnLeg: PropTypes.bool,
    addressIsEditable: PropTypes.bool,
    ctaConfig: PropTypes.string.isRequired,
    shipNowUrl: PropTypes.string,
    shipNowButtonText: PropTypes.string,
    shipToUrl: PropTypes.string,
    shipToButtonText: PropTypes.string,
    shipFromUrl: PropTypes.string,
    shipFromButtonText: PropTypes.string,
    backgroundPosition: PropTypes.string,
    backgroundMobile: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
    backgroundDesktop: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
    navLogo: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }).isRequired,
    allowedOriginLocations: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      formattedAddress: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired
    })),
    allowedDestinationLocations: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      formattedAddress: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired
    })),
    javascript: PropTypes.string,
    menuItems: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      inlineStyles: PropTypes.string,
    }).isRequired),
    microSite: PropTypes.shape({
      id: PropTypes.string.isRequired,
      subDomain: PropTypes.string,
    }),
    hero: PropTypes.shape({
      h1: PropTypes.string,
      h2: PropTypes.string,
      h3: PropTypes.string,
      h4: PropTypes.string,
      header: PropTypes.string.isRequired,
      subheader: PropTypes.string.isRequired,
      widgetHeader: PropTypes.string,
      breakText: PropTypes.string.isRequired,
      buttonText: PropTypes.string.isRequired,
      customClasses: PropTypes.string,
      buttonCustomClasses: PropTypes.string,
      limitToCityState: PropTypes.bool,
      url: PropTypes.string,
    }).isRequired,
    sections: PropTypes.arrayOf(PropTypes.shape({
      typeOf: PropTypes.string.isRequired,
      formType: PropTypes.string,
      header: PropTypes.string,
      headerClasses: PropTypes.string,
      subheader: PropTypes.string,
      subheaderClasses: PropTypes.string,
      body: PropTypes.string,
      bodyClasses: PropTypes.string,
      containerClasses: PropTypes.string,
      showLogo: PropTypes.bool,
      customClasses: PropTypes.string,
      htmlId: PropTypes.string,
      animate: PropTypes.bool,
      backgroundDesktop: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
      backgroundColor: PropTypes.string,
      image: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }),
      imageMobile: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }),
      backgroundPosition: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.shape({
        header: PropTypes.string.isRequired,
        headerClasses: PropTypes.string,
        body: PropTypes.string.isRequired,
        bodyClasses: PropTypes.string,
        linkUrl: PropTypes.string,
        linkText: PropTypes.string,
        linkCustomClasses: PropTypes.string,
        image: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }),
        imageStyles: PropTypes.string,
      })).isRequired,
    })).isRequired,
    footer: PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.string,
      showNewsletter: PropTypes.bool,
      customClass: PropTypes.string,
      supportPhoneNumber: PropTypes.string,
    })
  }).isRequired,
};

export default Page;
